import { afterNextRender, Component, DestroyRef, effect, ElementRef, EventEmitter, HostListener, Injector, input, InputSignal, OnDestroy, OnInit, Output, signal, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { PickerComponent } from '@ctrl/ngx-emoji-mart';
import { EmojiEvent } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { ConnectionPositionPair, FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

import { SHARED_MODULES } from '../../../shared.';
import { EmojiPanelRef, EmojiPanelSyncService } from '../../services/emoji-panel-sync.service';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';

type EmojiSelectPayload = EmojiEvent | { emoji?: { native?: string } };
type SkinTone = 1 | 2 | 3 | 4 | 5 | 6;

const RECENT_EMOJIS_STORAGE_KEY = 'ph-emoji-panel-recent';
const MAX_RECENT_EMOJIS = 24;
const SKIN_STORAGE_KEY = 'ph-emoji-panel-skin';

const I18N_KEYS = {
    search: 'EMOJI_PANEL.SEARCH',
    emojilist: 'EMOJI_PANEL.EMOJILIST',
    notfound: 'EMOJI_PANEL.NOTFOUND',
    clear: 'EMOJI_PANEL.CLEAR',
    categories: {
        search: 'EMOJI_PANEL.CATEGORIES.SEARCH',
        recent: 'EMOJI_PANEL.CATEGORIES.RECENT',
        people: 'EMOJI_PANEL.CATEGORIES.PEOPLE',
        nature: 'EMOJI_PANEL.CATEGORIES.NATURE',
        foods: 'EMOJI_PANEL.CATEGORIES.FOODS',
        activity: 'EMOJI_PANEL.CATEGORIES.ACTIVITY',
        places: 'EMOJI_PANEL.CATEGORIES.PLACES',
        objects: 'EMOJI_PANEL.CATEGORIES.OBJECTS',
        symbols: 'EMOJI_PANEL.CATEGORIES.SYMBOLS',
        flags: 'EMOJI_PANEL.CATEGORIES.FLAGS',
        custom: 'EMOJI_PANEL.CATEGORIES.CUSTOM'
    },
    skintones: {
        1: 'EMOJI_PANEL.SKINTONES.DEFAULT',
        2: 'EMOJI_PANEL.SKINTONES.LIGHT',
        3: 'EMOJI_PANEL.SKINTONES.MEDIUM_LIGHT',
        4: 'EMOJI_PANEL.SKINTONES.MEDIUM',
        5: 'EMOJI_PANEL.SKINTONES.MEDIUM_DARK',
        6: 'EMOJI_PANEL.SKINTONES.DARK'
    }
} as const;

export interface EmojiMartI18n {
    search: string;
    emojilist: string;
    notfound: string;
    clear: string;
    categories: Record<string, string>;
    skintones: Record<string, string>;
}

function buildI18n(t: (key: string) => string): EmojiMartI18n {
    return {
        search: t(I18N_KEYS.search),
        emojilist: t(I18N_KEYS.emojilist),
        notfound: t(I18N_KEYS.notfound),
        clear: t(I18N_KEYS.clear),
        categories: Object.fromEntries(
            Object.entries(I18N_KEYS.categories).map(([k, key]) => [k, t(key)])
        ) as Record<string, string>,
        skintones: Object.fromEntries(
            Object.entries(I18N_KEYS.skintones).map(([k, key]) => [k, t(key)])
        ) as Record<string, string>
    };
}

@Component({
    selector: 'ph-emoji-panel',
    templateUrl: './ph-emoji-panel.component.html',
    styleUrls: ['./ph-emoji-panel.component.scss'],
	encapsulation: ViewEncapsulation.None,
    imports: [...SHARED_MODULES, PickerComponent, PhIconWrapperComponent, PhIconComponent]
})
export class PhEmojiPanelComponent implements OnInit, OnDestroy, EmojiPanelRef {
    panelOpen: WritableSignal<boolean> = signal(false);
    recentEmojis: WritableSignal<Array<string>> = signal<Array<string>>([]);
    skin: WritableSignal<SkinTone> = signal<SkinTone>(1);

    overlayMode: InputSignal<boolean> = input<boolean>(false);
    open: InputSignal<boolean> = input<boolean>(false);
    origin: InputSignal<HTMLElement | ElementRef<HTMLElement>> = input<HTMLElement | ElementRef<HTMLElement>>();

    @ViewChild('dropdownContent') dropdownContentRef: TemplateRef<unknown>;

    private overlayRef: OverlayRef | null = null;
    private overlayCloseRequestedBySync: boolean = false;

    private readonly overlayPositions: Array<ConnectionPositionPair> = [
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
        { originX: 'start', originY: 'center', overlayX: 'start', overlayY: 'center' },
        { originX: 'end', originY: 'center', overlayX: 'end', overlayY: 'center' }
    ];

    readonly pickerStyle: Record<string, string> = {
        width: '100%',
        border: 'none',
        boxShadow: 'none'
    };

    readonly includedCategories: Array<string> = [
		'recent', 'people', 'nature', 'foods', 'activity', 'places', 'objects', 'symbols', 'flags', 'custom'
	];
    readonly totalFrequentLines: number = 2;
    readonly emojiSize: number = 24;
    readonly perLine: number = 8;

    i18n: EmojiMartI18n = buildI18n((): string => '');

    @Output() emojiPick: EventEmitter<string> = new EventEmitter<string>();
    @Output() panelClose: EventEmitter<void> = new EventEmitter<void>();

    constructor(
        private readonly elementRef: ElementRef<HTMLElement>,
        private readonly viewContainerRef: ViewContainerRef,
        private readonly overlay: Overlay,
        private readonly translate: TranslateService,
        private readonly injector: Injector,
        private readonly destroyRef: DestroyRef,
        private readonly emojiPanelSync: EmojiPanelSyncService
    ) {
        effect((): void => {
            const isOverlay: boolean = this.overlayMode();
            const currentlyOpen: boolean = this.isOpen();
            if (!currentlyOpen) {
                this.emojiPanelSync.clearIfActive(this);
                if (isOverlay) this.overlayCloseRequestedBySync = false;
                return;
            }
            this.emojiPanelSync.setActive(this);
        });

        effect((): void => {
            const isOverlay: boolean = this.overlayMode();
            const isOpen: boolean = this.open();
            const originInput: HTMLElement | ElementRef<HTMLElement> = this.origin();
            if (!isOverlay || !isOpen || !originInput) {
                this.detachOverlay();
                return;
            }
            const originEl: HTMLElement = this.getOriginElement(originInput);
            if (this.dropdownContentRef) {
                this.attachOverlay(originEl);
            } else {
                afterNextRender((): void => {
                    const o: HTMLElement | ElementRef<HTMLElement> = this.origin();
                    if (this.open() && this.dropdownContentRef && o) this.attachOverlay(this.getOriginElement(o));
                }, { injector: this.injector });
            }
        });
    }

    private getOriginElement(origin: HTMLElement | ElementRef<HTMLElement>): HTMLElement {
        return origin instanceof ElementRef ? origin.nativeElement : origin;
    }

    ngOnInit(): void {
        this.loadRecentEmojis();
        this.loadSkinPreference();
        this.updateI18n();
        this.translate.onLangChange
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((): void => this.updateI18n());
    }

    private updateI18n(): void {
        const t: (key: string) => string = (key: string): string => this.translate.instant(key);
        this.i18n = buildI18n(t);
    }

    private loadSkinPreference(): void {
        const stored: string = localStorage.getItem(SKIN_STORAGE_KEY);
        const n: number = stored ? parseInt(stored, 10) : NaN;
        if (n >= 1 && n <= 6) this.skin.set(n as SkinTone);
    }

    private saveSkinPreference(skin: SkinTone): void {
        localStorage.setItem(SKIN_STORAGE_KEY, String(skin));
    }

    onTriggerClick(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.panelOpen.update((v: boolean): boolean => {
            const next: boolean = !v;
            if (next) {
                afterNextRender((): void => this.refreshVirtualizedList(this.elementRef.nativeElement), { injector: this.injector });
            }
            return next;
        });
    }

    onEmojiSelect(payload: EmojiSelectPayload): void {
        const emoji: string = payload?.emoji?.native;
        if (!emoji) {
            this.closePanel();
            return;
        }
        this.emojiPick.emit(emoji);
        const emojiId: string | undefined = (payload?.emoji as { id?: string } | undefined)?.id;
        if (emojiId) this.addToRecentEmojis(emojiId);
        this.closePanel();
    }

    onSkinChange(skin: SkinTone): void {
        this.skin.set(skin);
        this.saveSkinPreference(skin);
    }

    private loadRecentEmojis(): void {
        const stored: string = localStorage.getItem(RECENT_EMOJIS_STORAGE_KEY);
        if (!stored) return;
        try {
            const parsed = JSON.parse(stored) as unknown;
            if (Array.isArray(parsed)) this.recentEmojis.set((parsed as string[]).slice(0, MAX_RECENT_EMOJIS));
        } catch {}
    }

    private addToRecentEmojis(emojiId: string): void {
        const next: Array<string> = [
			emojiId, ...this.recentEmojis().filter((id: string): boolean => id !== emojiId)]
			.slice(0, MAX_RECENT_EMOJIS);
        this.recentEmojis.set(next);
        localStorage.setItem(RECENT_EMOJIS_STORAGE_KEY, JSON.stringify(next));
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as Node;
        if (!this.overlayMode()) {
            if (!this.panelOpen() || this.elementRef.nativeElement.contains(target)) return;
        } else {
            if (!this.open()) return;
            if (this.elementRef.nativeElement.contains(target) || this.overlayRef?.overlayElement?.contains(target)) return;
        }
        this.closePanel();
    }

    @HostListener('keydown.escape', ['$event'])
    onEscape(event: KeyboardEvent): void {
        const isOpen: boolean = this.isOpen();
        if (!isOpen) return;
        event.preventDefault();
        event.stopPropagation();
        this.closePanel();
    }

    closePanel(): void {
        this.emojiPanelSync.clearIfActive(this);
        if (this.overlayMode()) {
            this.panelClose.emit();
            return;
        }
        this.panelOpen.set(false);
    }

    public closeBySync(): void {
        if (this.overlayMode()) {
            if (!this.open() || this.overlayCloseRequestedBySync) return;
            this.overlayCloseRequestedBySync = true;
            this.panelClose.emit();
            return;
        }
        if (this.panelOpen()) this.panelOpen.set(false);
    }

    private isOpen(): boolean {
        return this.overlayMode() ? this.open() : this.panelOpen();
    }

    private attachOverlay(originEl: HTMLElement): void {
        if (this.overlayRef?.hasAttached()) return;
        if (!this.dropdownContentRef) return;

        const positionStrategy: FlexibleConnectedPositionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(originEl)
            .withPositions(this.overlayPositions)
            .withViewportMargin(8)
            .withPush(true);

        this.overlayRef = this.overlay.create({
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            hasBackdrop: false,
            panelClass: 'ph-emoji-panel-overlay',
            width: 'min(360px, 90vw)',
            maxHeight: '80vh'
        });

        this.overlayRef.attach(new TemplatePortal(this.dropdownContentRef, this.viewContainerRef));
        this.refreshVirtualizedList(this.overlayRef.overlayElement);
    }

    private refreshVirtualizedList(container: HTMLElement): void {
        setTimeout((): void => {
            const scrollEl: HTMLElement | null = container.querySelector('.emoji-mart-scroll');
            if (!scrollEl) return;
            const scrollTop: number = scrollEl.scrollTop;
            scrollEl.scrollTop = scrollTop + 1;
            scrollEl.scrollTop = scrollTop;
            scrollEl.dispatchEvent(new Event('scroll'));
        });
    }

    private detachOverlay(): void {
        if (this.overlayRef) {
            this.overlayRef.detach();
            this.overlayRef.dispose();
            this.overlayRef = null;
        }
    }
    
    ngOnDestroy(): void {
        this.emojiPanelSync.clearIfActive(this);
        this.detachOverlay();
    }
}